import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// ============================================================
// Auto-refresh interceptor — when any API call returns 401,
// silently call /auth/refresh, then retry the original request.
// Prevents the user from being kicked out every 15 minutes.
// ============================================================
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed() {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalReq = error.config;
    const status = error.response?.status;
    const url = originalReq?.url || '';

    // Only auto-refresh for actual API calls (not login/refresh/logout itself)
    const skipRefreshFor = ['/auth/login', '/auth/refresh', '/auth/logout'];
    const shouldSkip = skipRefreshFor.some((p) => url.includes(p));

    if (status === 401 && !originalReq._retry && !shouldSkip) {
      originalReq._retry = true;

      if (isRefreshing) {
        // A refresh is already in flight — queue this request and resume after it
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(() => {
            axios(originalReq).then(resolve).catch(reject);
          });
        });
      }

      isRefreshing = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        isRefreshing = false;
        onRefreshed();
        return axios(originalReq);
      } catch (refreshErr) {
        isRefreshing = false;
        refreshSubscribers = [];
        // Refresh failed — surface the original 401 so the UI can redirect to /login
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
      return true;
    } catch (error) {
      setUser(false);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshToken, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
